// NR-5 — the cards in a Short form review (Specs/Launch Reviews — New Review Builder — PRD (Nov 4,
// 2026).md). The author pastes posts and emails or uploads text files. A line of dashes, a "Post 3"
// or "Email 2" heading, or a new "Subject:" line starts the next card. Plain rules, no AI, so what
// the author wrote is exactly what reviewers read. A card with a subject shows in the email frame.
import { useRef, useState } from "react";

import type { ReviewItem } from "./api";

export type CardRow = { key: string; id: string; title: string; subject: string; preview: string; body: string };

let seq = 0;
const row = (id: string, c: Partial<CardRow>): CardRow => ({ key: `c${++seq}`, id, title: "", subject: "", preview: "", body: "", ...c });
const RULE = /^\s*(-{3,}|={3,}|\*{3,})\s*$/;
const HEADING = /^\s*#{0,3}\s*\**((?:post|email)\s+\d+)\**\s*(?:[:.—–-]\s*(.*?))?\**\s*$/i;
const SUBJECT = /^\s*\**subject(?:\s+line)?\**\s*:\**\s*(.*)$/i;
const PREVIEW = /^\s*\**preview(?:\s+text)?\**\s*:\**\s*(.*)$/i;

const firstLine = (s: string) => { const l = s.trim().split("\n")[0].trim(); return l.length > 70 ? `${l.slice(0, 67).trim()}…` : l; };

// one piece of text -> one card: pulls out the heading, subject and preview lines; the rest is the body
const toCard = (lines: string[]): Partial<CardRow> | null => {
  let title = "", subject = "", preview = "";
  const rest: string[] = [];
  for (const line of lines) {
    const started = rest.some((l) => l.trim());
    const h = !started && !title && !subject ? HEADING.exec(line) : null;
    const s = !started && !subject ? SUBJECT.exec(line) : null;
    const p = !started && !preview ? PREVIEW.exec(line) : null;
    if (h) title = (h[2] || h[1]).trim();
    else if (s) subject = s[1].trim();
    else if (p) preview = p[1].trim();
    else rest.push(line);
  }
  const body = rest.join("\n").trim();
  if (!body && !subject) return null;
  return { title: title || subject || firstLine(body), subject, preview, body };
};

export function splitPieces(text: string): Partial<CardRow>[] {
  const pieces: string[][] = [[]];
  const has = (ls: string[]) => ls.some((l) => l.trim());
  // a Subject line straight after a heading belongs to that heading's card
  const hasBody = (ls: string[]) => ls.some((l) => l.trim() && !HEADING.test(l));
  for (const line of String(text || "").replace(/\r\n?/g, "\n").split("\n")) {
    const cur = pieces[pieces.length - 1];
    if (RULE.test(line)) { pieces.push([]); continue; }
    if (HEADING.test(line) && has(cur)) { pieces.push([line]); continue; }
    if (SUBJECT.test(line) && hasBody(cur)) { pieces.push([line]); continue; }
    cur.push(line);
  }
  return pieces.map(toCard).filter(Boolean) as Partial<CardRow>[];
}

// what an uploaded file holds, as plain text. One file is one card, unless its text has the markers above.
const EXT = /\.(txt|md|markdown|eml|html?)$/i;
export function fileText(name: string, raw: string): string {
  if (/\.eml$/i.test(name)) {
    const text = raw.replace(/\r\n?/g, "\n");
    const cut = text.indexOf("\n\n");
    const head = cut < 0 ? text : text.slice(0, cut);
    const subject = /^subject:\s*(.*)$/im.exec(head)?.[1] || "";
    return `${subject ? `Subject: ${subject}\n` : ""}${cut < 0 ? "" : text.slice(cut + 2)}`;
  }
  if (/\.html?$/i.test(name)) {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    doc.querySelectorAll("script,style").forEach((n) => n.remove());
    return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  }
  return raw;
}

export const rowsFromCards = (items: ReviewItem[]): CardRow[] =>
  items.map((it) => { const v = Object.values(it.versions || {})[0] || {}; return row(it.id, { title: it.title || "", subject: v.subject || "", preview: v.preview || "", body: v.body || "" }); });

// a card keeps its id once it has one; new ones get the next free c-number
export const itemsFromCards = (rows: CardRow[], taken: Iterable<string> = []): ReviewItem[] => {
  const live = rows.filter((r) => r.body.trim() || r.subject.trim());
  const used = new Set([...taken, ...live.map((r) => r.id).filter(Boolean)]);
  let n = 0;
  const next = () => { do n++; while (used.has(`c${n}`)); used.add(`c${n}`); return `c${n}`; };
  return live.map((r) => ({
    id: r.id || next(),
    title: r.title.trim() || r.subject.trim() || firstLine(r.body) || "Untitled",
    kind: r.subject.trim() ? "Email" : "Post",
    versions: { v1: { ...(r.subject.trim() ? { subject: r.subject.trim() } : {}), ...(r.preview.trim() ? { preview: r.preview.trim() } : {}), body: r.body.trim() } },
  }) as ReviewItem);
};

const MAX_FILE = 2 * 1024 * 1024;

type Props = { rows: CardRow[]; onChange: (fn: (rows: CardRow[]) => CardRow[]) => void };

export function BuilderCards({ rows, onChange }: Props) {
  const [pasting, setPasting] = useState(false);
  const [text, setText] = useState("");
  const [problem, setProblem] = useState("");
  const input = useRef<HTMLInputElement>(null);

  const addPieces = (pieces: Partial<CardRow>[]) => onChange((rs) => [...rs, ...pieces.map((p) => row("", p))]);
  const addPaste = () => {
    const pieces = splitPieces(text);
    if (!pieces.length) return setProblem("Paste a post or an email first.");
    addPieces(pieces); setText(""); setPasting(false); setProblem("");
  };
  const upload = async (list: FileList | null) => {
    const files = Array.from(list || []);
    if (input.current) input.current.value = "";
    setProblem("");
    const wrong: string[] = [];
    for (const f of files) {
      if (!EXT.test(f.name) || f.size > MAX_FILE) { wrong.push(f.name); continue; }
      const pieces = splitPieces(fileText(f.name, await f.text()));
      if (!pieces.length) { wrong.push(f.name); continue; }
      addPieces(pieces.length === 1 && !pieces[0].subject ? [{ ...pieces[0], title: f.name.replace(/\.[^.]+$/, "") }] : pieces);
    }
    if (wrong.length) setProblem(`We couldn't read ${wrong.join(", ")}. Upload text, Markdown, HTML or .eml files, or paste the words in.`);
  };

  const edit = (i: number, patch: Partial<CardRow>) => onChange((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, by: number) => onChange((rs) => { const to = i + by; if (to < 0 || to >= rs.length) return rs; const ys = [...rs]; [ys[i], ys[to]] = [ys[to], ys[i]]; return ys; });

  return (
    <section className="nr-sec" aria-label="Posts and emails">
      <h2>Posts and emails <small>{rows.length}</small></h2>
      {!rows.length && <p className="nr-hint">Add the posts and emails people should review. Each one becomes its own card.</p>}
      <ol className="nr-qs">
        {rows.map((r, i) => (
          <li key={r.key} className="nr-q">
            <div className="nr-qtop">
              <span className="nr-n">{i + 1}</span>
              <input aria-label={`Title for card ${i + 1}`} value={r.title} placeholder="What people should call this" onChange={(e) => edit(i, { title: e.target.value })} />
              <span className="nr-qtools">
                <button type="button" aria-label={`Move card ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                <button type="button" aria-label={`Move card ${i + 1} down`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}>↓</button>
                <button type="button" aria-label={`Delete card ${i + 1}`} className="danger" onClick={() => onChange((rs) => rs.filter((_, j) => j !== i))}>✕</button>
              </span>
            </div>
            <input aria-label={`Subject for card ${i + 1}`} value={r.subject} placeholder="Email subject (leave empty for a post)" onChange={(e) => edit(i, { subject: e.target.value })} />
            {r.subject.trim() && <input aria-label={`Preview for card ${i + 1}`} value={r.preview} placeholder="Preview text (optional)" onChange={(e) => edit(i, { preview: e.target.value })} />}
            <textarea aria-label={`Words for card ${i + 1}`} rows={5} value={r.body} placeholder="The post or email" onChange={(e) => edit(i, { body: e.target.value })} />
          </li>
        ))}
      </ol>
      {problem && <p className="nr-problem" role="alert">{problem}</p>}
      {pasting ? (
        <div className="nr-paste">
          <label className="nr-field">
            <span>Paste your posts and emails</span>
            <textarea aria-label="Paste posts and emails" rows={10} value={text} autoFocus onChange={(e) => setText(e.target.value)} />
          </label>
          <p className="nr-hint">To make several cards at once, put a line of dashes (---) between them, or start each with “Post 1”, “Email 2” or “Subject:”.</p>
          <div className="nr-row">
            <button type="button" className="nr-primary" onClick={addPaste}>Add cards</button>
            <button type="button" className="nr-ghost" onClick={() => { setPasting(false); setProblem(""); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="nr-row">
          <button type="button" className="nr-add" onClick={() => setPasting(true)}>Paste posts and emails</button>
          <button type="button" className="nr-ghost" onClick={() => input.current?.click()}>Upload files</button>
          <button type="button" className="nr-ghost" onClick={() => onChange((rs) => [...rs, row("", {})])}>Write one</button>
          <input ref={input} type="file" hidden multiple accept=".txt,.md,.markdown,.eml,.html,.htm" aria-label="Upload posts and emails" onChange={(e) => upload(e.target.files)} />
        </div>
      )}
    </section>
  );
}
